import React, { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import SharedHelpModal from '@shared/ui/HelpModal';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import { useHelpContent } from '../hooks/useHelpContent';
import MarkdownRenderer from './MarkdownRenderer';

const normalizeHeading = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Contextual entries open with a heading repeating their frontmatter title (the
 * format in HELP_CONTENT_GUIDE.md), which keeps each file readable on its own.
 * The modal already shows that title in its header, so drop the duplicate here
 * rather than churning every content file.
 *
 * Only a leading heading that MATCHES the title is removed — an entry that opens
 * on a different heading keeps it.
 */
export function stripLeadingTitle(markdown: string, title?: string): string {
  if (!markdown || !title) return markdown;

  const lines = markdown.split('\n');
  const firstIndex = lines.findIndex(line => line.trim() !== '');
  if (firstIndex === -1) return markdown;

  const match = lines[firstIndex].match(/^#{1,3}\s+(.+?)\s*$/);
  if (!match || normalizeHeading(match[1]) !== normalizeHeading(title)) return markdown;

  return lines.slice(firstIndex + 1).join('\n').replace(/^\s*\n/, '');
}

interface HelpModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Content key for loading help content */
  contentKey: string;
  /** Optional custom title override */
  title?: string;
}

/**
 * Contextual help, rendered through the shared <HelpModal>.
 *
 * The layout it replaces was a hand-rolled portal that set
 * `document.body.style.overflow` for its scroll lock. That silently did nothing
 * — index.css sets `overflow: auto !important` on body to neutralise Ionic's
 * structure.css, and an inline style loses to !important. The shared dialog uses
 * a class, and ref-counts it so a help modal opened over a form does not restore
 * page scrolling when it closes.
 *
 * `lg`, not the shared default `sm`: these entries are full articles with
 * headings and lists, not a sentence of explanation.
 */
const HelpModal: React.FC<HelpModalProps> = ({
  isOpen,
  onClose,
  contentKey,
  title: titleOverride
}) => {
  const { title, content, loading, error } = useHelpContent(contentKey);
  const displayTitle = titleOverride || title || 'Help';
  const body = useMemo(() => stripLeadingTitle(content, titleOverride || title), [content, titleOverride, title]);

  return (
    <SharedHelpModal
      isOpen={isOpen}
      title={displayTitle}
      onClose={onClose}
      size="lg"
      closeLabel="Got it"
      action={
        <Link
          to="/help"
          className="mr-auto inline-flex items-center gap-2 text-sm text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          onClick={onClose}
        >
          <ExternalLink className="h-4 w-4" />
          View Help Center
        </Link>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <p className="mb-4">{error}</p>
          <Link
            to="/help"
            className="text-primary-600 hover:underline dark:text-primary-400"
            onClick={onClose}
          >
            Browse Help Center
          </Link>
        </div>
      ) : (
        <MarkdownRenderer content={body} />
      )}
    </SharedHelpModal>
  );
};

export default HelpModal;






